import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBeforeUnload, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useGlobal } from '../context/global/global.context';
import { GlobalActions } from '../context/global/types';
import { commercialProcessesService } from '../services/commercial-processes.service';
export const useCommercialProcessContext = ({ processId, setData, }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { state, dispatch } = useGlobal();
    const { commercialProcessSelected, country } = state;
    const [mode, setMode] = useState('new');
    const [initialCountry] = useState(country);
    const hasProcessId = processId !== 'new' && processId !== '';
    const { data: commercialProcessContext, isLoading: isLoadingCommercialProcessContext } = useSWR(hasProcessId ? `/commercial-processes/${processId}` : null, () => commercialProcessesService.getCommercialProcessContextById(processId), {
        onError: () => {
            navigate('/hunter-tools/commercial-processes');
            dispatch({
                type: GlobalActions.showNotification,
                payload: {
                    type: 'error',
                    message: t('commercialProcess.notification.getContext.error'),
                },
            });
        },
    });
    useBeforeUnload(useCallback((event) => {
        if (commercialProcessSelected &&
            (location.pathname === `/hunter-tools/commercial-processes/new` ||
                location.pathname === `/hunter-tools/commercial-processes/${processId}`)) {
            event.preventDefault();
            return t('commercialProcess.exitConfirmation');
        }
    }, [commercialProcessSelected, processId]));
    useEffect(() => () => {
        dispatch({
            type: GlobalActions.setCommercialProcessSelected,
            payload: null,
        });
        dispatch({
            type: GlobalActions.setCommercialProcessesById,
            payload: {
                list: [],
                data: undefined,
            },
        });
        setData({});
    }, [dispatch, setData]);
    useEffect(() => {
        if (initialCountry !== country) {
            navigate('/hunter-tools/commercial-processes');
        }
    }, [initialCountry, country]);
    useEffect(() => {
        if (commercialProcessContext) {
            const { steps, processName, initialData, processData, processResources, status } = commercialProcessContext;
            if (status === 'COMPLETED' || status === 'REVIEW' || status === 'CANCELED') {
                setMode('only-view');
            }
            if (status === 'REJECTED' || status === 'ERROR') {
                setMode('edit');
            }
            dispatch({
                type: GlobalActions.setCommercialProcessSelected,
                payload: {
                    steps,
                    key: processName,
                    reason: [],
                    disabled: false,
                },
            });
            dispatch({
                type: GlobalActions.setCommercialProcessesById,
                payload: {
                    list: [],
                    data: {
                        country: initialData.country,
                        business: initialData.business,
                    },
                },
            });
            setData({
                files: processResources
                    .filter((r) => r.type === 'FILE')
                    .reduce((acc, r) => {
                    var _a, _b;
                    const currentId = parseInt(r.id);
                    const existingFile = acc[r.payload.type];
                    if (!existingFile || currentId > existingFile.id) {
                        acc[r.payload.type] = {
                            id: currentId,
                            file_details: Object.assign(Object.assign({}, r.payload), { file_name: r.payload.fileName, additional_data: {
                                    extension: `.${(_b = (_a = r.payload.fileName.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : ''}`,
                                } }),
                        };
                    }
                    return acc;
                }, {}),
                bankAccountSelected: commercialProcessContext.initialData.business.paymentReferences.find((p) => { var _a; return p.id.toString() === ((_a = processData.paymentReference) === null || _a === void 0 ? void 0 : _a.replace(/\D+/g, '')); }),
                bankInformationFormData: {
                    accountNumber: processData.account.number,
                    accountType: processData.account.accountType,
                    accountOwnerName: processData.account.holderName,
                    accountOwnerId: processData.account.identification,
                    accountOwnerIdType: processData.account.identificationType,
                    bankingEntity: processData.account.bankID,
                },
            });
        }
    }, [commercialProcessContext, dispatch, setData]);
    const shouldShowLoader = isLoadingCommercialProcessContext;
    const shouldRedirectToFinder = processId === 'new' && !commercialProcessSelected;
    return {
        contextId: commercialProcessContext === null || commercialProcessContext === void 0 ? void 0 : commercialProcessContext.id,
        mode,
        status: commercialProcessContext === null || commercialProcessContext === void 0 ? void 0 : commercialProcessContext.status,
        reasons: commercialProcessContext === null || commercialProcessContext === void 0 ? void 0 : commercialProcessContext.reasons,
        shouldShowLoader,
        shouldRedirectToFinder,
    };
};
